import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { AttendanceStatusOptions, CustomSelect, FilterOptions, TermFilter } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/daily-attendance-report/DailyAttendanceReportAction";
import { useDailyAttendanceReportUIContext } from "../DailyAttendanceReportUIContext";

const prepareFilter = (queryParams, values) => {
  let { term, date, dateEnd, sectionId, studentId, courseHour, attendanceStatus, studentNumber, advisorId, teacherId, parentName } = values;
  courseHour = courseHour === "" ? null : courseHour;
  studentNumber = studentNumber === "" ? null : studentNumber;
  const newQueryParams = { ...queryParams };
  const filter = { date, dateEnd, sectionId, studentId, courseHour, attendanceStatus, studentNumber, advisorId, teacherId, parentName };
  newQueryParams.filter = filter;
  newQueryParams.term = term;
  return newQueryParams;
};

export function DailyAttendanceReportFilter() {
  // Daily Attendance Report UI Context
  const dailyAttendanceReportUIContext = useDailyAttendanceReportUIContext();
  const dailyAttendanceReportUIProps = useMemo(() => {
    return {
      setQueryParams: dailyAttendanceReportUIContext.setQueryParams,
      queryParams: dailyAttendanceReportUIContext.queryParams,
    };
  }, [dailyAttendanceReportUIContext]);

  const dispatch = useDispatch();
  const { currentActiveTerm, sections, students, advisors, teachers } = useSelector(
    (state) => ({
      currentActiveTerm: state.auth.user.currentActiveTerm,
      sections: state.dailyAttendanceReport.sections,
      students: state.dailyAttendanceReport.students,
      advisors: state.dailyAttendanceReport.advisors,
      teachers: state.dailyAttendanceReport.teachers
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(actions.getAllList());
  }, [dispatch]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(dailyAttendanceReportUIProps.queryParams, values);
    if (!isEqual(newQueryParams, dailyAttendanceReportUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      dailyAttendanceReportUIProps.setQueryParams(newQueryParams);
    }
  };

  const sectionOptions = FilterOptions.concat(sections);
  const studentOptions = FilterOptions.concat(students);
  const advisorOptions = FilterOptions.concat(advisors);
  const teacherOptions = FilterOptions.concat(teachers);
  const attendanceStatusOptions = FilterOptions.concat(AttendanceStatusOptions.slice(1));

  return (
    <Formik
      initialValues={{
        term: currentActiveTerm,
        courseHour: "",
        studentNumber: "",
        parentName: ""
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right">
          <TermFilter
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            handleChange={handleChange}
            onChange={e => {
              if (values.sectionId) dispatch(actions.getList(e.target.value, values.sectionId));
              else dispatch(actions.getAdvisorsAndTeachers(e.target.value));
              setFieldValue("studentId", undefined);
              setFieldValue("advisorId", undefined);
              setFieldValue("teacherId", undefined);
            }}
            values={values}
          />
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Tarih
            </label>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="date"
                placeholder="Başlangıç"
                onBlur={handleBlur}
                value={values.date}
                onChange={(e) => {
                  setFieldValue("date", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="dateEnd"
                placeholder="Bitiş"
                onBlur={handleBlur}
                value={values.dateEnd}
                onChange={(e) => {
                  setFieldValue("dateEnd", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Şube
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={sectionOptions}
                value={values.sectionId}
                placeholder="Şube"
                onBlur={handleBlur}
                onChange={e => {
                  if (e.value) dispatch(actions.getStudents(values.term, e.value));
                  dispatch(actions.cleanStudentsState());
                  setFieldValue("sectionId", e.value);
                  setFieldValue("studentId", undefined);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Öğrenci
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={studentOptions}
                value={values.studentId}
                placeholder="Öğrenci"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("studentId", e.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="courseHour"
                min={1}
                placeholder="Ders Saati"
                onBlur={handleBlur}
                value={values.courseHour}
                onChange={(e) => {
                  setFieldValue("courseHour", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ders Saati</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={attendanceStatusOptions}
                value={values.attendanceStatus}
                placeholder="Yoklama Durumu"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("attendanceStatus", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Yoklama Durumu</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="studentNumber"
                min={1}
                placeholder="Öğrenci Numarası"
                onBlur={handleBlur}
                value={values.studentNumber}
                onChange={(e) => {
                  setFieldValue("studentNumber", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Öğrenci Numarası</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={advisorOptions}
                value={values.advisorId}
                placeholder="Danışman Öğretmenler"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("advisorId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Danışman Öğretmenler</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={teacherOptions}
                value={values.teacherId}
                placeholder="Yoklama Alan Öğretmen"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("teacherId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Yoklama Alan Öğretmen</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="parentName"
                placeholder="Veli Adı Soyadı"
                onBlur={handleBlur}
                value={values.parentName}
                onChange={(e) => {
                  setFieldValue("parentName", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Veli Adı Soyadı</b>
              </small>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}